import { inject, observer } from "mobx-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { Cell, MultiSelect, Option } from "src/components";
import { QuestionStore, UserStore, AuthStore, ItemStores, DeliverableStore, TagStore, MilestoneStore } from "src/stores";
import { DataType, ItemType, QuestionPosition, DeliverablePosition } from "src/types";
import styled from "styled-components";


export interface IAdvSearchBarProps {
  authStore?:     AuthStore;
  delivStore?:    DeliverableStore;
  itemStores?:    ItemStores;
  questionStore?: QuestionStore;
  milestoneStore?: MilestoneStore;
  tagStore?:      TagStore;
  type:           ItemType;
  userStore?:     UserStore;
}

export const AdvSearchBar = inject("itemStores", "userStore", "authStore", "questionStore", "delivStore", "tagStore", "milestoneStore")(observer((props: IAdvSearchBarProps) => {
  const { itemStores, userStore, authStore, type, delivStore, questionStore, tagStore, milestoneStore } = props;
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = React.useState<boolean>(true);

  if(!itemStores[type].advSearch) return null;

  const filterTitles: {[type: string]: string[]} = {
    [DataType.Question]:    ["attributes", "position", "from", "to", "tags", "deliverables"],
    [DataType.Deliverable]: ["attributes", "to", "tags", "milestones"],
  };

  if(filterTitles.question.length > 6 || filterTitles.deliverable.length > 6) throw new Error("Re:design advanced filter grid for more than 6 filters!");

  const filterSections: {[type: string]: {[title: string]: React.ReactNode}} = {
    [DataType.Question]: {
      from: (
        <MultiSelect
          options      = {userStore.getUsersBySpace(authStore.getCurRoomID, "all").map(u => ({key: ""+u.id, label: u.getFullName, value: u}))}
          selected     = {questionStore.filterValues.to || []}
          onAdd        = { (key: Option) => {
            questionStore.setFilterValue("to", [...(questionStore.filterValues.to || []), key]);
            questionStore.updateQuery();
          }}
          onRemove     = { (key: Option) => {
            questionStore.setFilterValue("to", questionStore.filterValues.to.filter(o => o.key !== key.key));
            questionStore.updateQuery();
          }}
          label        = {t("to")}
        />
      ),
      to: (
        <MultiSelect
          options      = {userStore.getUsersBySpace(authStore.getCurRoomID, "all").map(u => ({key: ""+u.id, label: u.getFullName, value: u}))}
          selected     = {questionStore.filterValues.from || []}
          onAdd        = { (key: Option) => {
            questionStore.setFilterValue("from", [...(questionStore.filterValues.from || []), key]);
            questionStore.updateQuery();
          }}
          onRemove     = { (key: Option) => {
            questionStore.setFilterValue("from", questionStore.filterValues.from.filter(o => o.key !== key.key));
            questionStore.updateQuery();
          }}
          label        = {t("from")}
          />
      ),
      tags: (
        <MultiSelect
          options      = {tagStore.getRoomTags.map(t => ({key: ""+t.id, label: t.title, value: t}))}
          selected     = {questionStore.filterValues.tags || []}
          onAdd        = { (key: Option) => {
            questionStore.setFilterValue("tags", [...(questionStore.filterValues.tags || []), key]);
            questionStore.updateQuery();
          }}
          onRemove     = { (key: Option) => {
            questionStore.setFilterValue("tags", questionStore.filterValues.tags.filter(o => o.key !== key.key));
            questionStore.updateQuery();
          }}
          label        = {t("tag_plural")}
        />
      ),
      deliverables: (
        <MultiSelect
          options      = {delivStore.linkOptions.getArray.map(d => ({key: ""+d.id, label: d.title, value: d}))}
          selected     = {questionStore.filterValues.deliverables || []}
          onAdd        = { (key: Option) => {
            questionStore.setFilterValue("deliverables", [...(questionStore.filterValues.deliverables || []), key]);
            questionStore.updateQuery();
          }}
          onRemove     = { (key: Option) => {
            questionStore.setFilterValue("deliverables", questionStore.filterValues.deliverables.filter(o => o.key !== key.key));
            questionStore.updateQuery();
          }}
          label        = {t("deliverable_plural")}
          onOpenSelect = {delivStore.linkOptions.load}
        />
      ),
      position: (
        <MultiSelect
          options      = {Object.values(QuestionPosition).map(p => ({key: p, label: t(p), value: p}))}
          selected     = {questionStore.filterValues.position || []}
          onAdd        = { (key: Option) => {
            questionStore.setFilterValue("position", [...(questionStore.filterValues.position || []), key]);
            questionStore.updateQuery();
          }}
          onRemove     = { (key: Option) => {
            questionStore.setFilterValue("position", questionStore.filterValues.position.filter(o => o.key !== key.key));
            questionStore.updateQuery();
          }}
          label        = {t("position")}
        />
      ),
      attributes: (
        <MultiSelect
          options = {[
            { key: "critical", value: "critical", label: t("critical") },
            { key: "answered", value: "answered", label: t("answered") },
          ]}
          selected     = {questionStore.filterValues.attributes || []}
          onAdd        = { (key: Option) => {
            questionStore.setFilterValue("attributes", [...(questionStore.filterValues.attributes || []), key]);
            questionStore.updateQuery();
          }}
          onRemove     = { (key: Option) => {
            questionStore.setFilterValue("attributes", questionStore.filterValues.attributes.filter(o => o.key !== key.key));
            questionStore.updateQuery();
          }}
          label        = {t("attributes")}
        />
      )
    },
    [DataType.Deliverable]: {
      attributes: (
        <MultiSelect
          options      = {Object.values(DeliverablePosition).map(p => ({key: p, label: t(p), value: p}))}
          selected     = {Object.values(DeliverablePosition).filter(p => {
            if( delivStore.filterValues[p] && delivStore.filterValues[p][0].key ) return true
            return false;
          }).map(p => ({key: p, label: t(p), value: p}))}
          onAdd        = { (key: Option) => {
            // Board means that the deliv is not delivered or done
            if(key.key == 'board') {
              delivStore.setFilterValue('board', [{key: true}], false)
              delivStore.setFilterValue('delivered', [{key: false}], false)
              delivStore.setFilterValue('done', [{key: false}], false)
            } else {
              delivStore.setFilterValue('delivered', [{key: false}], false);
              delivStore.setFilterValue('done', [{key: false}], false);
              delivStore.setFilterValue('board', [{key: false}], false);
              // set actual chosen.
              delivStore.setFilterValue(key.key, [{key: true}], false)
            }
            delivStore.updateQuery();
          }}
          onRemove     = { (key: Option) => {
            delete delivStore.filterValues[key.key];
            delivStore.updateQuery();
          }}
          label        = {t("attributes")}
        />
      ),
      to: (
        <MultiSelect
          options      = {userStore.getUsersBySpace(authStore.getCurRoomID, "all").map(u => ({key: ""+u.id, label: u.getFullName, value: u}))}
          selected     = {delivStore.filterValues.to || []}
          onAdd        = { (key: Option) => {
            delivStore.setFilterValue("to", [...(delivStore.filterValues.to || []), key]);
            delivStore.updateQuery();
          }}
          onRemove     = { (key: Option) => {
            delivStore.setFilterValue("to", delivStore.filterValues.to.filter(o => o.key !== key.key));
            delivStore.updateQuery();
          }}
          label        = {t("to")}
        />
      ),
      tags: (
        <MultiSelect
          options      = {tagStore.getRoomTags.map(t => ({key: ""+t.id, label: t.title, value: t}))}
          selected     = {delivStore.filterValues.tags || []}
          onAdd        = { (key: Option) => {
            delivStore.setFilterValue("tags", [...(delivStore.filterValues.tags || []), key]);
            delivStore.updateQuery();
          }}
          onRemove     = { (key: Option) => {
            delivStore.setFilterValue("tags", delivStore.filterValues.tags.filter(o => o.key !== key.key));
            delivStore.updateQuery();
          }}
          label        = {t("tag_plural")}
        />
      ),
      milestones: (
        <MultiSelect
          options      = {milestoneStore.getItems.map(t => ({key: ""+t.id, label: t.title, value: t}))}
          selected     = {delivStore.filterValues.milestones || []}
          onAdd        = { (key: Option) => {
            delivStore.setFilterValue("milestones", [...(delivStore.filterValues.milestones || []), key]);
            delivStore.updateQuery();
          }}
          onRemove     = { (key: Option) => {
            delivStore.setFilterValue("milestones", delivStore.filterValues.milestones.filter(o => o.key !== key.key));
            delivStore.updateQuery();
          }}
          label        = {t("milestone_plural")}
        />
      ),
    }
  }

  return (
    <Container expanded={expanded}>
        <Cell width={12 - (2*filterTitles[type].length)} />
        {filterTitles[type].map(f => (
          <FilterSection key={f}>
            {filterSections[type][f]}
          </FilterSection>
        ))}
    </Container>
  );
}));

const Container = styled.div<{expanded: boolean}>`
  display:         flex;
  justify-content: flex-start;
  flex-direction:  row;
  flex-wrap:       wrap;
  row-gap:         1rem;
`;

const FilterSection = styled.div`
  margin-right: 1.8rem;
`